import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Transfer {
  transfer_id: string | null;
  request_id?: string | null;
  asset_id: string;
  asset_name: string;
  serial_number: string;
  asset_type: string;
  from_business_id: string;
  from_business_name: string;
  to_business_id: string;
  to_business_name: string;
  transferred_by: string;
  transferred_by_name: string;
  transferred_by_email: string;
  transfer_status: 'pending' | 'in_transit' | 'delivered' | 'cancelled';
  delivery_check_id: string | null;
  delivery_check_name: string | null;
  delivery_check_status: number | null;
  created_at: string;
  initiated_at?: string; // Legacy field for backward compatibility
  completed_at: string | null;
  notes: string | null;
  metadata: any;
  transfer_source?: 'direct' | 'request';
  request_status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
  request_notes?: string | null;
  response_notes?: string | null;
  delivery_completed_at: string | null;
  delivery_completed_by: string | null;
  delivery_completed_by_name: string | null;
  delivery_responses_count: number;
}

export default function TransferHistory() {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [responsesDialogOpen, setResponsesDialogOpen] = useState(false);
  const [responses, setResponses] = useState<any[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [transferToCancel, setTransferToCancel] = useState<Transfer | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchTransferHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/multi-business-manager/transfer-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTransfers(response.data.transfers || []);
      } else {
        setError(response.data.error || 'Failed to load transfer history');
      }
    } catch (err: any) {
      console.error('Error fetching transfer history:', err);
      setError(err.response?.data?.error || 'Failed to load transfer history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransferHistory();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'in_transit':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_transit':
        return <PendingIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isDeliveryCheckCompleted = (transfer: Transfer): boolean => {
    // Check multiple indicators of completion
    if (transfer.delivery_completed_at) return true;
    if (transfer.delivery_check_status === 100) return true;
    if (transfer.transfer_status === 'delivered' && transfer.delivery_responses_count > 0) return true;
    return false;
  };

  const handleViewDetails = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setDetailsDialogOpen(true);
  };

  const handleViewResponses = async (transfer: Transfer) => {
    if (!transfer.delivery_check_id) {
      return;
    }
    
    try {
      setLoadingResponses(true);
      setResponses([]);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/deliveries/${transfer.delivery_check_id}/responses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResponses(response.data.responses || []);
        setSelectedTransfer(transfer);
        setResponsesDialogOpen(true);
      } else {
        setError(response.data.error || 'Failed to load responses');
      }
    } catch (err: any) {
      console.error('Error fetching responses:', err);
      setError(err.response?.data?.error || 'Failed to load responses');
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleCancelClick = (transfer: Transfer) => {
    setTransferToCancel(transfer);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!transferToCancel) return;

    try {
      setCancelling(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/multi-business-manager/transfer/${transferToCancel.transfer_id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setError(null);
        setCancelDialogOpen(false);
        setTransferToCancel(null);
        // Refresh the transfer history
        await fetchTransferHistory();
      } else {
        setError(response.data.error || 'Failed to cancel transfer');
      }
    } catch (err: any) {
      console.error('Error cancelling transfer:', err);
      setError(err.response?.data?.error || 'Failed to cancel transfer');
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelDialogClose = () => {
    if (!cancelling) {
      setCancelDialogOpen(false);
      setTransferToCancel(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Transfer History
          </Typography>
          <IconButton onClick={fetchTransferHistory} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View all asset transfers across all businesses you manage
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h4" color="primary">
              {transfers.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Transfers
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h4" color="success.main">
              {transfers.filter(t => t.transfer_status === 'delivered').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Delivered
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h4" color="warning.main">
              {transfers.filter(t => t.transfer_status === 'pending' || t.transfer_status === 'in_transit').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h4" color="info.main">
              {transfers.filter(t => t.delivery_check_id).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              With Delivery Checks
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Transfers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>From Business</TableCell>
              <TableCell>To Business</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Delivery Check</TableCell>
              <TableCell>Transferred By</TableCell>
              <TableCell>Initiated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No transfers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transfers.map((transfer) => (
                <TableRow key={transfer.transfer_id || transfer.request_id || transfer.asset_id} hover>
                  <TableCell>
                    {transfer.transfer_source === 'request' ? (
                      <Chip 
                        label="Request" 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        title="Transfer requested via mobile app"
                      />
                    ) : (
                      <Chip 
                        label="Direct" 
                        size="small" 
                        color="default" 
                        variant="outlined"
                        title="Direct transfer by multi-business manager"
                      />
                    )}
                    {transfer.request_status && transfer.request_status !== 'approved' && (
                      <Chip 
                        label={transfer.request_status.toUpperCase()} 
                        size="small" 
                        color={transfer.request_status === 'rejected' ? 'error' : 'warning'}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {transfer.asset_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transfer.serial_number} • {transfer.asset_type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{transfer.from_business_name}</TableCell>
                  <TableCell>{transfer.to_business_name}</TableCell>
                  <TableCell>
                    {(() => {
                      const icon = getStatusIcon(transfer.transfer_status);
                      return (
                        <Chip
                          {...(icon && { icon })}
                          label={transfer.transfer_status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(transfer.transfer_status) as any}
                          size="small"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {transfer.delivery_check_id ? (
                      <Box>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                          {transfer.delivery_responses_count} response{transfer.delivery_responses_count !== 1 ? 's' : ''}
                        </Typography>
                        {isDeliveryCheckCompleted(transfer) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleViewResponses(transfer)}
                          >
                            View Responses
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No check
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {transfer.transferred_by_name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transfer.transferred_by_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(transfer.created_at || transfer.initiated_at || null)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(transfer)}
                        color="primary"
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {(transfer.transfer_status === 'pending' || transfer.transfer_status === 'in_transit') && (
                        <IconButton
                          size="small"
                          onClick={() => handleCancelClick(transfer)}
                          color="error"
                          title="Cancel Transfer"
                        >
                          <CancelIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTransfer && (
          <>
            <DialogTitle>
              Transfer Details
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                {selectedTransfer.asset_name}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Asset Name
                  </Typography>
                  <Typography variant="body1">{selectedTransfer.asset_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body1">{selectedTransfer.serial_number}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Asset Type
                  </Typography>
                  <Typography variant="body1">{selectedTransfer.asset_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Transfer Source
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransfer.transfer_source === 'request' ? (
                      <Chip label="Mobile Request" size="small" color="info" variant="outlined" />
                    ) : (
                      <Chip label="Direct Transfer" size="small" color="default" variant="outlined" />
                    )}
                  </Typography>
                </Grid>
                {selectedTransfer.request_status && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Request Status
                    </Typography>
                    <Chip
                      label={selectedTransfer.request_status.toUpperCase()}
                      color={
                        selectedTransfer.request_status === 'approved' ? 'success' :
                        selectedTransfer.request_status === 'rejected' ? 'error' :
                        selectedTransfer.request_status === 'pending' ? 'warning' : 'default'
                      }
                      size="small"
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  {(() => {
                    const icon = getStatusIcon(selectedTransfer.transfer_status);
                    return (
                      <Chip
                        {...(icon && { icon })}
                        label={selectedTransfer.transfer_status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(selectedTransfer.transfer_status) as any}
                        size="small"
                      />
                    );
                  })()}
                </Grid>
                {selectedTransfer.request_notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Request Notes
                    </Typography>
                    <Typography variant="body1">{selectedTransfer.request_notes}</Typography>
                  </Grid>
                )}
                {selectedTransfer.response_notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Response Notes
                    </Typography>
                    <Typography variant="body1">{selectedTransfer.response_notes}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    From Business
                  </Typography>
                  <Typography variant="body1">{selectedTransfer.from_business_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    To Business
                  </Typography>
                  <Typography variant="body1">{selectedTransfer.to_business_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Transferred By
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransfer.transferred_by_name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedTransfer.transferred_by_email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Initiated At
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedTransfer.created_at || selectedTransfer.initiated_at || null)}</Typography>
                </Grid>
                {selectedTransfer.completed_at && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Completed At
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedTransfer.completed_at)}</Typography>
                  </Grid>
                )}
                {selectedTransfer.delivery_check_id && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Delivery Check Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Check Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransfer.delivery_check_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Check Status
                      </Typography>
                      <Chip
                        label={isDeliveryCheckCompleted(selectedTransfer) ? 'Completed' : 'Pending'}
                        color={isDeliveryCheckCompleted(selectedTransfer) ? 'success' : 'warning'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Responses Count
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransfer.delivery_responses_count}
                      </Typography>
                    </Grid>
                    {selectedTransfer.delivery_completed_at && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Completed By
                        </Typography>
                        <Typography variant="body1">
                          {selectedTransfer.delivery_completed_by_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(selectedTransfer.delivery_completed_at)}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {selectedTransfer.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">{selectedTransfer.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedTransfer?.delivery_check_id && (
                <Button 
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    handleViewResponses(selectedTransfer);
                  }}
                  variant="outlined"
                  color="primary"
                >
                  View Responses
                </Button>
              )}
              <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Responses Dialog */}
      <Dialog
        open={responsesDialogOpen}
        onClose={() => setResponsesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Delivery Check Responses
          {selectedTransfer && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              {selectedTransfer.asset_name} • {selectedTransfer.serial_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingResponses ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : responses.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No responses found
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {responses.map((item, index) => (
                <Card key={item.question.question_id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Question {index + 1}
                      {item.question.required && (
                        <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                      {item.question.question_text}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Type: {item.question.question_type.replace('_', ' ').toUpperCase()}
                    </Typography>
                  </Box>
                  
                  {item.response ? (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Response by: {item.response.user_name || 'Unknown'}
                        </Typography>
                        <Chip
                          label={item.response.response_status || 'complete'}
                          color={
                            item.response.response_status === 'complete' || item.response.response_status === 'completed'
                              ? 'success'
                              : 'warning'
                          }
                          size="small"
                        />
                      </Box>
                      
                      <Paper sx={{ p: 2, bgcolor: 'background.default', mt: 1 }}>
                        {((() => {
                          const qType = (item.question.question_type || '').toLowerCase();
                          const qText = (item.question.question_text || '').toLowerCase();
                          const rt = item.response?.response_text;
                          const rtStr = typeof rt === 'string' ? rt.trim() : (rt && typeof rt === 'object' ? JSON.stringify(rt) : '');
                          const isSigType = qType === 'signature' || qType === 'scribble_signature' || qText.includes('signature');
                          const looksLikeScribble = !!rtStr && rtStr.startsWith('{') && rtStr.includes('"paths"');
                          const looksLikeImage = !!rtStr && (rtStr.startsWith('data:image/') || (rtStr.length > 200 && !rtStr.startsWith('{') && /^[A-Za-z0-9+/=\s]+$/.test(rtStr)));
                          return isSigType || looksLikeScribble || looksLikeImage;
                        })()) ? (
                          <Box>
                            {item.response?.response_text ? (() => {
                              // Try to parse signature data
                              let signature: any = null;
                              let isBase64SVG = false;
                              let parsed: any = null;
                              const rawRt = item.response.response_text;
                              const rtForParse = typeof rawRt === 'string' ? rawRt : JSON.stringify(rawRt);
                              
                              try {
                                parsed = typeof rawRt === 'object' && rawRt !== null ? rawRt : JSON.parse(rtForParse);
                                
                                // Check if it's a base64 SVG format {name, signature}
                                if (parsed && typeof parsed === 'object' && typeof parsed.signature === 'string' && parsed.signature.startsWith('data:image/svg+xml;base64,')) {
                                  isBase64SVG = true;
                                }
                                // Check if it's a scribble signature format {paths, width, height}
                                else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.paths)) {
                                  signature = parsed;
                                }
                              } catch (error) {
                                // Not JSON, might be plain text or other format
                                console.log('Signature data is not JSON:', error);
                              }
                              
                              // Render base64 SVG
                              if (isBase64SVG && parsed) {
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1,
                                        p: 2,
                                        bgcolor: 'white',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                        }
                                      }}
                                      onClick={() => {
                                        const popup = window.open('', '_blank', 'width=600,height=400');
                                        if (popup) {
                                          popup.document.write(`
                                            <html>
                                              <head><title>Signature - ${parsed.name || item.response.user_name || 'User'}</title></head>
                                              <body style="margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
                                                <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                                                  <h3 style="margin-top: 0; color: #333; text-align: center;">Signature - ${parsed.name || item.response.user_name || 'User'}</h3>
                                                  <img src='${parsed.signature}' alt="SVG Signature" style="max-width: 100%; max-height: 350px; border: 1px solid #ddd; border-radius: 4px;" />
                                                </div>
                                              </body>
                                            </html>
                                          `);
                                        }
                                      }}
                                      title="Click to view full size signature"
                                    >
                                      <img
                                        src={parsed.signature}
                                        alt="SVG Signature"
                                        style={{ maxWidth: '100%', maxHeight: 200, display: 'block', margin: '0 auto' }}
                                      />
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 8,
                                          backgroundColor: 'rgba(0,122,255,0.9)',
                                          color: 'white',
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}
                                      >
                                        <Box component="span">🔍</Box>
                                        VIEW FULL SIZE
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                      Digital signature captured on device. Click to view full size.
                                    </Typography>
                                  </Box>
                                );
                              }
                              
                              // Render scribble signature (paths format)
                              if (signature && signature.paths && Array.isArray(signature.paths)) {
                                const { width = 400, height = 200, paths, timestamp } = signature;
                                
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1,
                                        p: 2,
                                        bgcolor: 'white',
                                        minHeight: 150,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                        }
                                      }}
                                      onClick={() => {
                                        const popup = window.open('', '_blank', 'width=600,height=400');
                                        if (popup) {
                                          popup.document.write(`
                                            <html>
                                              <head><title>Signature - ${item.response.user_name || 'User'}</title></head>
                                              <body style="margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
                                                <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                                                  <h3 style="margin-top: 0; color: #333; text-align: center;">Signature - ${item.response.user_name || 'User'}</h3>
                                                  ${timestamp ? `<p style="text-align: center; color: #666; font-size: 14px;">Signed on: ${new Date(timestamp).toLocaleString()}</p>` : ''}
                                                  <svg width="${width}" height="${height}" style="border: 1px solid #ddd; border-radius: 4px;">
                                                    ${paths.map((path: string) => 
                                                      `<path d="${path}" stroke="#007AFF" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />`
                                                    ).join('')}
                                                  </svg>
                                                </div>
                                              </body>
                                            </html>
                                          `);
                                        }
                                      }}
                                      title="Click to view full size signature"
                                    >
                                      <svg 
                                        width="100%" 
                                        height="150" 
                                        viewBox={`0 0 ${width} ${height}`}
                                        style={{ display: 'block' }}
                                      >
                                        {paths.map((path: string, index: number) => (
                                          <path
                                            key={index}
                                            d={path}
                                            stroke="#007AFF"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        ))}
                                      </svg>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 8,
                                          backgroundColor: 'rgba(0,122,255,0.9)',
                                          color: 'white',
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}
                                      >
                                        <Box component="span">🔍</Box>
                                        VIEW FULL SIZE
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                      Digital signature captured on device. Click to view full size.
                                    </Typography>
                                  </Box>
                                );
                              }
                              
                              // Plain base64/data-URI image (e.g. a PNG signature saved directly
                              // as the answer) - render it as an image instead of a wall of
                              // base64 text or a generic "format not recognized" message.
                              const rawText = item.response.response_text.trim();
                              const looksLikeBase64Image =
                                rawText.startsWith('data:image/') ||
                                (rawText.length > 200 && /^[A-Za-z0-9+/=\s]+$/.test(rawText));
                              if (looksLikeBase64Image) {
                                const src = rawText.startsWith('data:') ? rawText : `data:image/png;base64,${rawText}`;
                                return (
                                  <Box
                                    sx={{
                                      border: '2px solid',
                                      borderColor: 'primary.main',
                                      borderRadius: 1,
                                      p: 2,
                                      bgcolor: 'white',
                                      display: 'flex',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <img
                                      src={src}
                                      alt="Signature"
                                      style={{ maxWidth: '100%', maxHeight: 200, display: 'block' }}
                                    />
                                  </Box>
                                );
                              }

                              // Fallback for other formats
                              return (
                                <Box
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: 'white',
                                    minHeight: 100,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    Signature data available (format not recognized)
                                  </Typography>
                                </Box>
                              );
                            })() : (
                              <Box
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  p: 2,
                                  bgcolor: 'white',
                                  minHeight: 100,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  No signature provided
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : item.question.question_type === 'yes_no' || item.question.question_type === 'yes_no_na' ? (
                          <Typography variant="body1" fontWeight={500}>
                            {item.response.response_text || 'N/A'}
                          </Typography>
                        ) : item.question.question_type === 'checkbox' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: 1,
                                bgcolor: item.response.response_text === 'true' || item.response.response_text?.toLowerCase() === 'yes' ? 'success.main' : 'grey.300',
                                border: '2px solid',
                                borderColor: item.response.response_text === 'true' || item.response.response_text?.toLowerCase() === 'yes' ? 'success.main' : 'grey.400',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              {(item.response.response_text === 'true' || item.response.response_text?.toLowerCase() === 'yes') && '✓'}
                            </Box>
                            <Typography variant="body1">
                              {item.response.response_text || 'Not checked'}
                            </Typography>
                          </Box>
                        ) : item.question.question_type === 'multiple_choice' ? (
                          <Typography variant="body1">
                            {item.response.response_text || 'No selection'}
                          </Typography>
                        ) : (
                          <Typography variant="body1">
                            {item.response.response_text || 'No response provided'}
                          </Typography>
                        )}
                        
                        {item.response.additional_text && (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                            <Typography variant="caption" fontWeight={500} display="block" sx={{ mb: 0.5 }}>
                              Additional Information:
                            </Typography>
                            <Typography variant="body2">
                              {item.response.additional_text}
                            </Typography>
                          </Box>
                        )}
                        
                        {item.images && item.images.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" fontWeight={500} display="block" sx={{ mb: 1 }}>
                              Images ({item.images.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {item.images.map((img: any, imgIndex: number) => {
                                const imageUrl = img.image_path?.startsWith('http') 
                                  ? img.image_path 
                                  : `${API_BASE_URL.replace('/api', '')}${img.image_path}`;
                                return (
                                  <Box
                                    key={img.response_image_id || imgIndex}
                                    sx={{
                                      position: 'relative',
                                      width: 150,
                                      height: 150,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                      }
                                    }}
                                    onClick={() => window.open(imageUrl, '_blank')}
                                    title="Click to view full size"
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Response ${imgIndex + 1}`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                          parent.innerHTML = `
                                            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666;">
                                              <div style="text-align: center;">
                                                <div>📷</div>
                                                <div style="font-size: 12px;">Image unavailable</div>
                                              </div>
                                            </div>
                                          `;
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Submitted: {formatDate(item.response.created_at)}
                        </Typography>
                      </Paper>
                    </Box>
                  ) : (
                    <Paper sx={{ p: 2, bgcolor: 'background.default', mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No response provided
                      </Typography>
                    </Paper>
                  )}
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponsesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Transfer Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Cancel Transfer
        </DialogTitle>
        <DialogContent>
          {transferToCancel && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to cancel this transfer? This action cannot be undone.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Asset
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {transferToCancel.asset_name}
                </Typography>
                {transferToCancel.serial_number && (
                  <Typography variant="caption" color="text.secondary">
                    {transferToCancel.serial_number}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  From Business
                </Typography>
                <Typography variant="body1">
                  {transferToCancel.from_business_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  To Business
                </Typography>
                <Typography variant="body1">
                  {transferToCancel.to_business_name}
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                The asset will be reverted back to <strong>{transferToCancel.from_business_name}</strong> if the transfer has already been processed.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDialogClose}
            disabled={cancelling}
            color="inherit"
          >
            Keep Transfer
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={cancelling}
            color="error"
            variant="contained"
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

